import React, { useState, useContext, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { usersRepository } from '../repositories/users_repository'
import { ErrorsContext } from '../contexts/errors_context'
import { AxiosError } from 'axios'
import { useLocation, useNavigate } from 'react-router-dom'
import queryString from 'query-string'
import { AuthContext } from '../contexts/auth_context'
import { GenericNotFound } from './generic_not_found'

type ResetPasswordFormData = {
  password: string
  password_confirmation: string
}

const ResetPassword = () => {
  const { t } = useTranslation()
  const { register, handleSubmit } = useForm<ResetPasswordFormData>()
  const { addError } = useContext(ErrorsContext)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const location = useLocation()
  const navigate = useNavigate()
  const queryParams = queryString.parse(location.search)
  const token = queryParams.token as string | undefined
  const { isAuthenticated } = useContext(AuthContext)

  const onSubmit = useCallback((data: ResetPasswordFormData) => {
    if (loading || !token) return

    setLoading(true)

    usersRepository.resetPassword({
      ...data,
      token,
    })
      .then(() => {
        const msg = t('account.reset_password.success_message')
        setMessage(msg)
      })
      .catch((error: AxiosError) => {
        addError?.(error)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [loading, token, addError, t])

  if (isAuthenticated) {
    return <GenericNotFound />
  }

  if (!token) {
    return <div className="narrow">
      <h1>{t('account.reset_password.title')}</h1>
      <p>{t('account.reset_password.invalid_token')}</p>
    </div>
  }

  if (message) {
    return <div className="narrow">
      <h1>{t('account.reset_password.title')}</h1>
      <p>{message}</p>
      <button className="full" onClick={() => navigate('/login')}>
        {t('account.reset_password.success_link')}
      </button>
    </div>
  }

  return (
    <div className="narrow">
      <h1>{t('account.reset_password.title')}</h1>
      <form onSubmit={handleSubmit(onSubmit)}>
        <p>
          <label>{t('account.reset_password.password')}</label>
          <input type="password" className="full" {...register('password')} required minLength={8} />
        </p>
        <p>
          <label>{t('account.reset_password.password_confirmation')}</label>
          <input type="password" className="full" {...register('password_confirmation')} required minLength={8} />
        </p>
        <p>
          <button className="full" disabled={loading}>
            {t('account.reset_password.submit')}
          </button>
        </p>
      </form>
    </div>
  )
}

export { ResetPassword }
